import { useMutation } from "@tanstack/react-query";
import { pollyApi } from "@/utils/axios/pollyUtils.ts";
import { SynthesizeParams } from "@/components/tools/Polly/types.ts";

export function usePolly() {
  // Mutation pour synthétiser du texte
  const synthesizeMutation = useMutation({
    mutationFn: (params: SynthesizeParams) => pollyApi.synthesize(params),
  });

  // Fonction pour jouer un audio à partir du contenu base64
  const playAudio = (audioContent: string, contentType: string) => {
    // Convertir base64 en Blob
    const byteCharacters = atob(audioContent);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    const audioUrl = URL.createObjectURL(blob);

    // Créer et jouer l'élément audio
    const audio = new Audio(audioUrl);

    // Nettoyer l'URL après la lecture
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
    };

    audio.play();

    return {
      audio,
      url: audioUrl,
      stop: () => {
        audio.pause();
        audio.currentTime = 0;
        URL.revokeObjectURL(audioUrl);
      },
    };
  };

  // Fonction pour synthétiser et jouer du texte
  const speakText = async (params: SynthesizeParams) => {
    try {
      const result = await synthesizeMutation.mutateAsync(params);
      if (result.success && result.audioContent) {
        return playAudio(result.audioContent, result.contentType);
      }
      throw new Error("La synthèse vocale a échoué");
    } catch (error) {
      console.error("Erreur lors de la lecture du texte:", error);
      throw error;
    }
  };

  // Obtenir un blob audio directement
  const getAudioBlob = async (params: SynthesizeParams) => {
    return await pollyApi.getSynthesizeBlob(params);
  };

  return {
    synthesize: synthesizeMutation.mutate,
    synthesizeAsync: synthesizeMutation.mutateAsync,
    speakText,
    getAudioBlob,
    isLoading: synthesizeMutation.isPending,
    error: synthesizeMutation.error,
    reset: synthesizeMutation.reset,
  };
}
